import {
    DocumentFunctionalityEnum,
    DocumentPairFunctionalityEnum,
    DocumentType,
    DocumentTypePair,
    UserQuery,
} from "../graphql-types";

export enum MainMenuGroupType {
    Invoice,
    DeliveryNote,
    Order,
    WorkOrder,
    Approval,
    Picking,
    Analysis,
    FixedAssets,
    Inventory,
    WarehouseReceipts,
    WorkflowTasks,
    DMSDocuments,
}

export enum MainMenuItemFeature {
    Warehouse,
    List,
    Create,
    UnfinishedDocuments,
    DailyIncome,
}

export class MainMenuItem {
    fromType: DocumentType;
    features: MainMenuItemFeature[] = [];

    toType?: DocumentType;
    pairFunctionality?: DocumentPairFunctionalityEnum;

    icon?: string;
    sameToDocument: MainMenuItem[] = [];

    constructor(
        fromType: DocumentType,
        features: MainMenuItemFeature[],
        toType?: DocumentType,
        pairFunctionality?: DocumentPairFunctionalityEnum,
    ) {
        this.fromType = fromType;
        this.features = features;
        this.toType = toType;
        this.pairFunctionality = pairFunctionality;

        switch (pairFunctionality) {
            case DocumentPairFunctionalityEnum.Picking:
                this.icon = "qrcode";
                break;
            case DocumentPairFunctionalityEnum.Copy:
                this.icon = "solidCopy";
                break;
        }
    }

    endDoc(): DocumentType {
        return this.toType ?? this.fromType;
    }
}

export class MainMenuGroup {
    type: MainMenuGroupType;
    items: MainMenuItem[] = [];
    singleItem: boolean;
    color: string;
    icon: string;
    singleGroup: boolean = false;
    noItems: boolean = true;

    constructor(type: MainMenuGroupType, items: MainMenuItem[]) {
        this.type = type;
        this.items = items;

        this.singleItem = items.length == 1;
        this.noItems = !this.items.length;
        this.color = getMainMenuGroupColor(type);
        this.icon = getMainMenuGroupIcon(type);
    }
}

export const makeMainMenuGroups = (userQuery: UserQuery): MainMenuGroup[] => {
    let ret: ICreateOrJustAddRes = {};
    let retList: MainMenuGroup[] = [];
    const documentTypes: DocumentType[] =
        (userQuery.user?.documentTypes as unknown as DocumentType[]) ?? [];
    const documentTypePairs: DocumentTypePair[] =
        (userQuery.user?.documentTypePairs as unknown as DocumentTypePair[]) ?? [];

    if (documentTypes.length || documentTypePairs.length) {
        for (const dt of documentTypes) {
            if (dt) {
                let group: MainMenuGroupType | undefined;

                if (dt.create || dt.read || dt.update || dt.delete) {
                    if (!dt.create) {
                        var alreadyHasCreate = documentTypePairs.find(
                            (e) => e.toId == dt.id,
                        );

                        if (
                            !alreadyHasCreate ||
                            dt.functionality ==
                                DocumentFunctionalityEnum.TmnDocumentsDirect ||
                            dt.functionality ==
                                DocumentFunctionalityEnum.TmnDocumentsWeb ||
                            dt.functionality ==
                                DocumentFunctionalityEnum.TmnDocumentsClassificationQueue
                        ) {
                            group = getMenuItemGroup(dt);
                        }
                    } else {
                        group = getMenuItemGroup(dt);
                    }
                }

                if (group != undefined) {
                    let features: MainMenuItemFeature[] = [];

                    if (dt.read) {
                        features.push(
                            MainMenuItemFeature.Warehouse,
                            MainMenuItemFeature.List,
                        );
                    }

                    if (dt.create) {
                        features.push(MainMenuItemFeature.Create); //MainMenuItemFeature.UnfinishedDocuments); // mislim da unfinished ne treba
                    }

                    if (
                        dt.functionality == DocumentFunctionalityEnum.LurInvoice ||
                        dt.adminShowDailyIncome
                    ) {
                        features.push(MainMenuItemFeature.DailyIncome);
                    }

                    if (dt.functionality == DocumentFunctionalityEnum.FixedAssets) {
                        features = features.filter((e) => e != MainMenuItemFeature.List);
                    }

                    if (
                        dt.functionality ==
                        DocumentFunctionalityEnum.MarservisUserWorkOrders
                    ) {
                        features = features.filter(
                            (e) =>
                                e != MainMenuItemFeature.Warehouse &&
                                e != MainMenuItemFeature.UnfinishedDocuments,
                        );
                    }

                    if (dt.functionality == DocumentFunctionalityEnum.WorkFlowTasks) {
                        features = [];
                        features.push(MainMenuItemFeature.List);
                    }

                    if (dt.hideWarehouseOverview) {
                        features = features.filter(
                            (e) => e != MainMenuItemFeature.Warehouse,
                        );
                    }

                    ret = _createOrJustAdd(ret, group, new MainMenuItem(dt, features));
                }
            }
        }

        for (const dtp of documentTypePairs) {
            if (dtp) {
                const fromType: DocumentType = documentTypes.find(
                    (e) => e.id == dtp.fromId,
                )!;
                const toType: DocumentType = documentTypes.find(
                    (e) => e.id == dtp.toId,
                )!;
                let group: MainMenuGroupType | undefined;

                switch (dtp.functionality) {
                    case DocumentPairFunctionalityEnum.Picking:
                        group = MainMenuGroupType.Picking;
                        break;
                    case DocumentPairFunctionalityEnum.Copy:
                        group = getMenuItemGroup(toType);
                        break;
                }

                if (group) {
                    let features: MainMenuItemFeature[] = [];

                    if (fromType.read) {
                        features.push(
                            MainMenuItemFeature.Warehouse,
                            MainMenuItemFeature.List,
                        );
                    }

                    features.push(MainMenuItemFeature.Create); //MainMenuItemFeature.UnfinishedDocuments); // mislim da unfinished ne treba

                    if (
                        toType.functionality == DocumentFunctionalityEnum.LurInvoice ||
                        toType.adminShowDailyIncome
                    ) {
                        features.push(MainMenuItemFeature.DailyIncome);
                    }

                    if (
                        toType.functionality ==
                        DocumentFunctionalityEnum.MarservisUserWorkOrders
                    ) {
                        features = features.filter(
                            (e) =>
                                e != MainMenuItemFeature.Warehouse &&
                                e != MainMenuItemFeature.UnfinishedDocuments,
                        );
                    }

                    if (toType.hideWarehouseOverview) {
                        features = features.filter(
                            (e) => e != MainMenuItemFeature.Warehouse,
                        );
                    }

                    ret = _createOrJustAdd(
                        ret,
                        group,
                        new MainMenuItem(fromType, features, toType, dtp.functionality),
                    );
                }
            }
        }

        retList = Object.keys(ret).map((e) => {
            return new MainMenuGroup(+e, ret[+e] ?? []);
        });

        retList = retList.sort((a, b) => a.type - b.type);

        retList = retList.map((e) => {
            e.items = e.items.sort((a, b) =>
                (a.toType?.type ?? a.fromType.type)!.localeCompare(
                    (b.toType?.type ?? b.fromType.type)!,
                ),
            );
            return e;
        });

        if (retList.length) {
            if (retList.length == 1) {
                retList[0].singleGroup = true;
            }
        }

        retList = retList.map((e) => {
            e.items = e.items.map((i) => {
                if (i.sameToDocument.length) {
                    i.sameToDocument = [i, ...i.sameToDocument];
                }
                return i;
            });
            return e;
        });
    }

    return retList;
};

type ICreateOrJustAddRes = Partial<{
    [key in keyof typeof MainMenuGroupType]: MainMenuItem[];
}>;

const _createOrJustAdd = (
    ret: ICreateOrJustAddRes,
    groupKey: MainMenuGroupType,
    mainMenuItem: MainMenuItem,
): ICreateOrJustAddRes => {
    const localRet = {...ret};
    if (!localRet[groupKey]) {
        localRet[groupKey] = [];
    } else {
        if (mainMenuItem.toType != null) {
            var found = false;

            const res: ICreateOrJustAddRes = Object.keys(localRet)
                .map((key) => {
                    const realKey = key as keyof typeof MainMenuGroupType;
                    localRet[realKey] = localRet[realKey]!.map((e) => {
                        if (
                            e.toType &&
                            e.toType?.id == mainMenuItem.toType?.id && // spajanje po ID a ne po type
                            e.pairFunctionality == mainMenuItem.pairFunctionality
                        ) {
                            e.sameToDocument.push(mainMenuItem);
                            found = true;
                        }
                        return e;
                    });
                    return localRet;
                })
                .reduce<ICreateOrJustAddRes>((a, b) => ({...a, ...b}), {});

            if (found) return res;
        }
    }

    if (localRet[groupKey]) localRet[groupKey]!.push(mainMenuItem);

    return localRet;
};

const getMenuItemGroup = (dt: {
    functionality: DocumentFunctionalityEnum;
}): MainMenuGroupType => {
    let group: MainMenuGroupType;
    if (dt != null) {
        switch (dt.functionality) {
            case DocumentFunctionalityEnum.MarservisUserWorkOrders:
            case DocumentFunctionalityEnum.WorkOrdersReception:
            case DocumentFunctionalityEnum.WorkOrdersOperative:
                group = MainMenuGroupType.WorkOrder;
                break;
            case DocumentFunctionalityEnum.Orders:
                group = MainMenuGroupType.Order;
                break;
            case DocumentFunctionalityEnum.DeliveryNotes:
                group = MainMenuGroupType.DeliveryNote;
                break;
            case DocumentFunctionalityEnum.LurInvoice:
            case DocumentFunctionalityEnum.InvoiceDeliveryNotes:
                group = MainMenuGroupType.Invoice;
                break;
            case DocumentFunctionalityEnum.Approvals:
                break;
            case DocumentFunctionalityEnum.DeliveryNotesControl:
                break;
            case DocumentFunctionalityEnum.DeliveryNotesFromOrder:
                break;
            case DocumentFunctionalityEnum.FixedAssets:
                group = MainMenuGroupType.FixedAssets;
                break;
            case DocumentFunctionalityEnum.Inventory:
                group = MainMenuGroupType.Inventory;
                break;
            case DocumentFunctionalityEnum.WarehouseReceipt:
                group = MainMenuGroupType.WarehouseReceipts;
                break;
            case DocumentFunctionalityEnum.WorkFlowTasks:
                group = MainMenuGroupType.WorkflowTasks;
                break;
            case DocumentFunctionalityEnum.TmnDocumentsDirect:
            case DocumentFunctionalityEnum.TmnDocumentsWeb:
            case DocumentFunctionalityEnum.TmnDocumentsClassificationQueue:
                group = MainMenuGroupType.DMSDocuments;
                break;
        }
    }
    return group!;
};

export const webDocumentsColors = {
    lightGray: "#CCCCCC",
    primary: "#812990",
    yellow: "#EABB30",
    primaryLight: "#b289be",
    blue: "#3F78A4",
    gray: "#8e8e93",
    orange: "#FF8757",
    green: "#3CAF7E",
    red: "#FD575B",
    lightBlue: "#94BBDC",
    cartonBrown: "#CD9F61",
    yellowColor: "#EABB30",
    pink: "#FF4081",
};

const getMainMenuGroupColor = (type: MainMenuGroupType): string => {
    switch (type) {
        case MainMenuGroupType.Invoice:
            return webDocumentsColors.orange;
        case MainMenuGroupType.DeliveryNote:
            return webDocumentsColors.blue;
        case MainMenuGroupType.Order:
            return webDocumentsColors.green;
        case MainMenuGroupType.WorkOrder:
            return webDocumentsColors.primaryLight;
        case MainMenuGroupType.Approval:
            return webDocumentsColors.blue;
        case MainMenuGroupType.Picking:
            return webDocumentsColors.yellowColor;
        case MainMenuGroupType.Analysis:
            return webDocumentsColors.red;
        case MainMenuGroupType.Inventory:
            return webDocumentsColors.cartonBrown;
        case MainMenuGroupType.FixedAssets:
            return webDocumentsColors.lightGray;
        case MainMenuGroupType.WarehouseReceipts:
            return webDocumentsColors.lightBlue;
        case MainMenuGroupType.WorkflowTasks:
            return webDocumentsColors.red;
        case MainMenuGroupType.DMSDocuments:
            return webDocumentsColors.pink;
    }
};

const getMainMenuGroupIcon = (type: MainMenuGroupType): string => {
    switch (type) {
        case MainMenuGroupType.Invoice:
        case MainMenuGroupType.DeliveryNote:
        case MainMenuGroupType.Order:
        case MainMenuGroupType.WorkOrder:
        case MainMenuGroupType.Approval:
        case MainMenuGroupType.WarehouseReceipts:
            return "Edit";
        case MainMenuGroupType.Picking:
            return "QRCode";
        case MainMenuGroupType.Analysis:
            return "chartBar";
        case MainMenuGroupType.FixedAssets:
            return "FixedAssetManagement";
        case MainMenuGroupType.Inventory:
            return "ProductCatalog";
        case MainMenuGroupType.WorkflowTasks:
            return "WorkFlow";
        case MainMenuGroupType.DMSDocuments:
            return "FabricOpenFolderHorizontal";
    }
};

export const getMainMenuGroupItemFeatureIcon = (
    type: MainMenuItemFeature,
): string => {
    switch (type) {
        case MainMenuItemFeature.Create:
            return "Add";
        case MainMenuItemFeature.DailyIncome:
            return "Money";
        case MainMenuItemFeature.List:
            return "Documentation";
        case MainMenuItemFeature.UnfinishedDocuments:
            return "DocumentReply";
        case MainMenuItemFeature.Warehouse:
            return "ProductList";
    }
};

export const shownWebDocumentFeatures = [
    MainMenuItemFeature.DailyIncome,
    MainMenuItemFeature.List,
    MainMenuItemFeature.UnfinishedDocuments,
    // MainMenuItemFeature.Warehouse
];
